import {useContext, useEffect, useMemo} from 'react';
import Honeybadger from '@honeybadger-io/js';
import StripeAccountContext from '../contexts/StripeAccountContext';
import ExperimentsContext from '../contexts/ExperimentsContext';
import MemberContext from '../contexts/MemberContext';
import stripeAccountLoadStates from '../constants/stripeAccount';

const reportedInvalidAssignments = new Set();

/**
 * Resolves the Financial Accounts flow after Stripe account ownership is known.
 * Existing accounts are pinned to their established version; only no-account
 * users are evaluated through Unleash and Flipper.
 */
const useFinancialAccounts = () => {
  const {stripeAccount, error, accountLoadState} = useContext(
    StripeAccountContext,
  );
  const {memberId} = useContext(MemberContext);
  const financialAccountsExperiment =
    useContext(ExperimentsContext)?.experiments?.financialAccounts ?? {};
  const {
    experimentId,
    variant,
    isExperimentLoading = true,
    isFeatureFlagEnabled = false,
    isFlagsReady = false,
    isInControl = false,
    isInVariant = false,
  } = financialAccountsExperiment;

  const shouldUseInvalidAssignmentFallback =
    accountLoadState !== stripeAccountLoadStates.LOADING &&
    !error &&
    !stripeAccount?.stripeId &&
    isFlagsReady &&
    !isExperimentLoading &&
    isFeatureFlagEnabled &&
    !isInControl &&
    !isInVariant &&
    variant != null;

  useEffect(() => {
    if (!shouldUseInvalidAssignmentFallback) return;

    const assignmentKey = `${memberId ?? 'missing'}:${experimentId ??
      'missing'}:${variant ?? 'missing'}`;

    if (reportedInvalidAssignments.has(assignmentKey)) return;

    Honeybadger.notify(
      new Error(
        `Financial Accounts experiment assignment fallback: experimentId=${experimentId ??
          'missing'}, variant=${variant ?? 'missing'}, memberId=${memberId ??
          'missing'}`,
      ),
      'FinancialAccountsExperimentAssignmentFallback',
    );
    reportedInvalidAssignments.add(assignmentKey);
  }, [experimentId, memberId, shouldUseInvalidAssignmentFallback, variant]);

  return useMemo(() => {
    /** Never infer a V1 flow before the Stripe account lookup finishes. */
    if (accountLoadState === stripeAccountLoadStates.LOADING) {
      return {isResolved: false, isEnabled: false};
    }

    if (error) {
      return {isResolved: true, isEnabled: false};
    }

    const hasStripeAccount = Boolean(stripeAccount?.stripeId);

    /** Existing Stripe accounts bypass experimentation and remain on their version. */
    if (hasStripeAccount) {
      return {
        isResolved: true,
        isEnabled: stripeAccount.accountVersion === 'v2',
      };
    }

    /** No-account users wait for both Unleash readiness and Flipper assignment. */
    if (!isFlagsReady || isExperimentLoading) {
      return {isResolved: false, isEnabled: false};
    }

    /** Flag-off and validated control assignments are final V1 decisions. */
    if (!isFeatureFlagEnabled || isInControl) {
      return {isResolved: true, isEnabled: false};
    }

    /** A validated variant assignment is the only experiment-driven V2 path. */
    if (isInVariant) {
      return {isResolved: true, isEnabled: true};
    }

    /** A settled invalid assignment falls back to the legacy V1 flow. */
    return {isResolved: true, isEnabled: false};
  }, [
    accountLoadState,
    error,
    stripeAccount,
    isFlagsReady,
    isExperimentLoading,
    isFeatureFlagEnabled,
    isInControl,
    isInVariant,
  ]);
};

export default useFinancialAccounts;
